import React, { useContext, useState } from "react";
import RegistrationContext from "../../contexts/RegistrationContext";
import { ArrowUpCircle } from "lucide-react";
import axios from "axios";
import { base_url, stripe_key } from "../../config";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(stripe_key);

function CheckoutForm({ amount, onBack, setMessage }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: base_url + "/api/upgrade_payment/" },
    });
    if (error) setMessage(error.message);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <PaymentElement options={{ layout: "tabs" }} />
      <div className="flex gap-3 justify-between items-center">
        <button type="button" onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 underline">
          ← Back
        </button>
        <button type="submit" disabled={!stripe} className="button">
          Pay {amount} €
        </button>
      </div>
    </form>
  );
}

export default function Upgrade() {
  const { prices } = useContext(RegistrationContext);

  const [clientSecret, setClientSecret] = useState("");
  const [amount, setAmount] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const studentPrice = prices?.student ?? 0;
  const standardPrice = prices?.standard ?? 0;
  const difference = standardPrice - studentPrice;

  const handleCheckout = async () => {
    setLoading(true);
    setMessage("");
    try {
      const res = await axios.post(`${base_url}/api/upgrade_checkout`);
      setClientSecret(res.data.clientSecret);
      const stripe = await stripePromise;
      const { paymentIntent } = await stripe.retrievePaymentIntent(res.data.clientSecret);
      setAmount(paymentIntent.amount / 100);
      setShowPayment(true);
    } catch (e) {
      setMessage("Unexpected error. " + (e.response?.data?.message ?? ""));
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100" style={{ background: "#f8faff" }}>
          <ArrowUpCircle size={18} color="#002E78" />
          <span className="font-semibold text-[#002E78]">Upgrade to Full Delegate</span>
        </div>

        <div className="px-6 py-5">
          {!showPayment ? (
            <>
              <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                Upgrade your registration from <strong>Student</strong> to <strong>Full Delegate</strong>.
                You will only be charged the difference between the two registration types at the current pricing phase.
              </p>

              {/* Price breakdown */}
              <div className="border border-gray-100 rounded-lg overflow-hidden mb-5">
                <div className="flex justify-between px-4 py-3 text-sm text-gray-600 border-b border-gray-100">
                  <span>Full Delegate price</span>
                  <span>{standardPrice} €</span>
                </div>
                <div className="flex justify-between px-4 py-3 text-sm text-gray-600 border-b border-gray-100">
                  <span>Already paid (Student)</span>
                  <span>− {studentPrice} €</span>
                </div>
                <div className="flex justify-between px-4 py-3 text-sm font-semibold text-[#002E78] bg-gray-50">
                  <span>Amount due</span>
                  <span>{difference} €</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCheckout}
                disabled={loading || difference <= 0}
                className="button disabled:opacity-50"
                style={{ height: "40px", padding: "0 20px" }}
              >
                {loading ? "Processing…" : `Upgrade — ${difference} €`}
              </button>
              {message && <p className="mt-3 text-red-500 text-sm">{message}</p>}
            </>
          ) : (
            <Elements options={{ clientSecret, appearance: { theme: "stripe" } }} stripe={stripePromise}>
              <CheckoutForm amount={amount} onBack={() => setShowPayment(false)} setMessage={setMessage} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
}
